import React from "react";
import { Facebook, Plus, X, Youtube } from "lucide-react";

import CourtToggle, {
  type CourtType,
} from "../../../../components/CourtToggle";
import type {
  LinkType,
  StreamLinkType,
  StreamSettingPayload,
  StreamSettingSelectionLabels,
} from "./StreamSetting.types";
import BenchStreamSettingsForm from "./BenchStreamSettingsForm";
import DistrictStreamSettingsForm from "./DistrictStreamSettingsForm";

interface StreamSettingsModalProps {
  courtType: CourtType;
  editId: string | null;
  formValues: StreamSettingPayload;
  linkType: LinkType[];
  onClose: () => void;
  onCourtTypeChange: (type: CourtType) => void;
  onFormValuesChange: (values: StreamSettingPayload) => void;
  onLinkTypeChange: (updater: (prev: LinkType[]) => LinkType[]) => void;
  onRemoveLink: (index: number) => void;
  onSaved: () => void;
  selectedLabels?: StreamSettingSelectionLabels;
}

const StreamSettingsModal: React.FC<StreamSettingsModalProps> = ({
  courtType,
  editId,
  formValues,
  linkType,
  onClose,
  onCourtTypeChange,
  onFormValuesChange,
  onLinkTypeChange,
  onRemoveLink,
  onSaved,
  selectedLabels,
}) => {
  const setField = <K extends keyof StreamSettingPayload>(
    field: K,
    value: StreamSettingPayload[K],
  ) => {
    onFormValuesChange({
      ...formValues,
      [field]: value,
    });
  };

  const addLink = (type: StreamLinkType, label: LinkType["label"]) => {
    onLinkTypeChange((prev) => [
      ...prev,
      {
        label,
        index: formValues.links.length,
      },
    ]);

    onFormValuesChange({
      ...formValues,
      links: [...formValues.links, { type, link: "", key: "" }],
    });
  };

  const updateLink = (index: number, field: "link" | "key", value: string) => {
    const links = formValues.links.map((link, currentIndex) =>
      currentIndex === index ? { ...link, [field]: value } : link,
    );

    setField("links", links);
  };

  const handleCourtTypeChange = (nextCourtType: CourtType) => {
    onCourtTypeChange(nextCourtType);
  };
  const title = editId ? "Edit Stream Settings" : "Add Stream Settings";
  const streamingLinks = (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-4">
        Streaming Links
      </h4>

      <div className="space-y-4">
        {formValues.links.map((item, index) => (
          <div
            key={index}
            className="relative bg-gray-50 border rounded-xl p-5"
          >
            <button
              onClick={() => onRemoveLink(index)}
              className="absolute top-2 right-3 text-gray-400 hover:text-red-600"
            >
              x
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  {linkType.find((link) => link.index === index)?.label ||
                    "Link"}
                </label>
                <input
                  type="text"
                  value={item.link}
                  onChange={(event) =>
                    updateLink(index, "link", event.target.value)
                  }
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  Stream Key
                </label>
                <input
                  type="text"
                  value={item.key}
                  onChange={(event) =>
                    updateLink(index, "key", event.target.value)
                  }
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        {!linkType.some((link) => link.label === "Facebook") && (
          <button
            onClick={() => addLink("facebook", "Facebook")}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
          >
            <Facebook size={18} />
            Facebook
          </button>
        )}

        {!linkType.some((link) => link.label === "Youtube") && (
          <button
            onClick={() => addLink("youtube", "Youtube")}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
          >
            <Youtube size={18} />
            YouTube
          </button>
        )}

        <button
          onClick={() => addLink("custom", "Custom Link")}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <Plus size={18} />
          Add More
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-[#9CA3AF] flex items-center justify-between shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            className="text-[#3e3e3e]  flex justify-center items-center  text-center w-8 h-8 rounded-full font-bold cursor-pointer border border-[#D0D0D0] hover:border-[#666666] hover:text-[#000000]"
          >
            <X size={20} />
          </button>
        </div>
        <div className="modal-scroll overflow-y-auto flex-1 px-6 pb-6">
          {!editId && (
          <div className="mt-4 flex items-center justify-center">
            <CourtToggle value={courtType} onChange={handleCourtTypeChange} />
          </div>
          )}

          {courtType === "high" ? (
            <BenchStreamSettingsForm
              editId={editId}
              formValues={formValues}
              onCancel={onClose}
              onFormValuesChange={onFormValuesChange}
              onSaved={onSaved}
              selectedLabels={selectedLabels}
            >
              {streamingLinks}
            </BenchStreamSettingsForm>
          ) : (
            <DistrictStreamSettingsForm
              editId={editId}
              formValues={formValues}
              onCancel={onClose}
              onFormValuesChange={onFormValuesChange}
              onSaved={onSaved}
              selectedLabels={selectedLabels}
            >
              {streamingLinks}
            </DistrictStreamSettingsForm>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamSettingsModal;
